const ALLOWED_SIGNUP_NEXT_ROOTS = [
  "/companion",
  "/today",
  "/profile",
  "/jobs",
] as const;
const ALLOWED_SIGNUP_NEXT_PREFIXES = ["/squad/invite/"] as const;
const ALLOWED_SIGNUP_NEXT_EXACT_PATHS = [
  "/community/events/submit",
  "/setup",
] as const;

export type SignupEntryIntent = "generic" | "companion" | "work";

export function resolveSignupEntryIntent(input: {
  explicitIntent?: string | null;
  next?: string | null;
}): SignupEntryIntent {
  if (input.explicitIntent === "companion") return "companion";
  if (input.explicitIntent === "work") return "work";

  const next = sanitiseOptionalSignupNext(input.next);
  if (next?.startsWith("/companion") || next?.startsWith("/today")) {
    return "companion";
  }
  if (next?.startsWith("/jobs") || next?.startsWith("/profile")) {
    return "work";
  }
  return "generic";
}

function isAllowedSignupNextPath(pathname: string): boolean {
  return (
    ALLOWED_SIGNUP_NEXT_EXACT_PATHS.some((path) => pathname === path) ||
    ALLOWED_SIGNUP_NEXT_ROOTS.some(
      (root) => pathname === root || pathname.startsWith(`${root}/`),
    ) ||
    ALLOWED_SIGNUP_NEXT_PREFIXES.some((prefix) => pathname.startsWith(prefix))
  );
}

export function sanitiseSignupNext(
  value: string | null | undefined,
  fallback = "/",
): string {
  if (typeof value !== "string") return fallback;

  const trimmed = value.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) {
    return fallback;
  }

  try {
    decodeURI(trimmed);
    const url = new URL(trimmed, "https://peakwave.local");
    if (url.origin !== "https://peakwave.local") return fallback;
    const path = `${url.pathname}${url.search}${url.hash}`;
    const isExactOnlyPath = ALLOWED_SIGNUP_NEXT_EXACT_PATHS.some(
      (allowedPath) => url.pathname === allowedPath,
    );
    if (isExactOnlyPath) {
      return ALLOWED_SIGNUP_NEXT_EXACT_PATHS.some(
        (allowedPath) => trimmed === allowedPath,
      )
        ? trimmed
        : fallback;
    }
    return isAllowedSignupNextPath(url.pathname) ? path : fallback;
  } catch {
    return fallback;
  }
}

export function sanitiseOptionalSignupNext(
  value: string | null | undefined,
): string | null {
  const sanitised = sanitiseSignupNext(value, "/");
  return sanitised === "/" ? null : sanitised;
}

export function sanitiseLocalLoginRedirect(
  value: string | null | undefined,
): string | null {
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) {
    return null;
  }

  try {
    decodeURI(trimmed);
    const url = new URL(trimmed, "https://peakwave.local");
    if (url.origin !== "https://peakwave.local") return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

export function buildSignupCallbackUrl(origin: string, next: string | null): string {
  const callback = new URL("/auth/callback", origin);
  if (next) {
    callback.searchParams.set("next", next);
  }
  return callback.toString();
}

export function buildEmailConfirmedLoginPath(next: string | null | undefined): string {
  const params = new URLSearchParams({ email_confirmed: "true" });
  const safeNext = sanitiseOptionalSignupNext(next);
  if (safeNext) {
    params.set("redirect", safeNext);
  }
  return `/login?${params.toString()}`;
}

export function resolveSignupCompletionDestination(options: {
  hasAvailabilityWindows: boolean;
  next: string | null;
  loginRedirect?: string | null;
  entryIntent?: SignupEntryIntent;
}): string {
  const explicitNext =
    sanitiseOptionalSignupNext(options.next) ??
    sanitiseOptionalSignupNext(options.loginRedirect);
  if (
    options.entryIntent === "work" &&
    options.hasAvailabilityWindows &&
    (!explicitNext || explicitNext === "/jobs")
  ) {
    return "/signup/crews";
  }

  if (explicitNext) return explicitNext;

  if (options.entryIntent === "companion") return "/companion";
  if (options.entryIntent === "work") return "/jobs";
  if (options.hasAvailabilityWindows) {
    return "/signup/crews";
  }

  return options.entryIntent === "generic" ? "/setup" : "/companion";
}

export function resolveExistingCandidateLoginDestination(
  redirect: string | null | undefined,
  options: {
    hasActionableItem?: boolean;
    lastVisitedArea?: string | null;
  } = {},
): string {
  const safeRedirect = sanitiseLocalLoginRedirect(redirect);
  if (safeRedirect) return safeRedirect;

  if (options.hasActionableItem) return "/jobs";

  const lastArea = sanitiseLocalLoginRedirect(options.lastVisitedArea);
  if (
    lastArea &&
    ["/jobs", "/conditions", "/companion", "/inbox"].some(
      (root) => lastArea === root || lastArea.startsWith(`${root}/`),
    )
  ) {
    return lastArea;
  }

  return "/companion";
}

export function resolveExistingEmployerLoginDestination(
  redirect: string | null | undefined,
): string {
  const safeRedirect = sanitiseLocalLoginRedirect(redirect);
  if (
    !safeRedirect ||
    safeRedirect === "/dashboard" ||
    sanitiseOptionalSignupNext(safeRedirect)
  ) {
    return "/employer/dashboard";
  }

  return safeRedirect;
}

const SIGNUP_SOURCE_MAX_LENGTH = 80;
const SIGNUP_SOURCE_PATTERN = /^[a-z0-9][a-z0-9:_.-]*$/i;

/**
 * First-party entry source for a signup link, for example `guide:meribel` or
 * `festival:glastonbury-jobs`. It names the PeakWave page or CTA that sent the
 * visitor, never a person, so it is stored with the signup regardless of
 * analytics consent. Anything outside the slug alphabet is dropped.
 */
export function sanitiseSignupSource(value: unknown): string | null {
  const raw = Array.isArray(value) ? value[0] : value;
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed || trimmed.length > SIGNUP_SOURCE_MAX_LENGTH) return null;
  return SIGNUP_SOURCE_PATTERN.test(trimmed) ? trimmed : null;
}

/**
 * Intent-preserving candidate signup link: `/signup?intent=work&next=...`.
 * `next` must be an allowed signup destination (usually a /jobs filter) and
 * falls back to `/jobs`, so the work setup step is never skipped.
 */
export function buildWorkSignupHref(
  options: { next?: string | null; source?: string | null } = {},
): string {
  const params = new URLSearchParams({ intent: "work" });
  params.set("next", sanitiseOptionalSignupNext(options.next ?? "/jobs") ?? "/jobs");
  const source = sanitiseSignupSource(options.source);
  if (source) params.set("source", source);
  return `/signup?${params.toString()}`;
}
